import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { formatDate } from "../lib/format";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Line,
    ComposedChart,
} from "recharts";

/**
 * AdminBilling — Faturamento & MRR (super-admin).
 * Segue o mesmo padrão visual da tela AdminClients (dark mode indigo/violet).
 *
 * Dados provisórios: agregação local a partir de `registrations` (localStorage).
 * Endpoints planejados (a implementar quando o backend real for construído):
 *   GET /admin/mrr/summary  → KPIs
 *   GET /admin/mrr/history  → série mensal (12m)
 *   GET /admin/mrr/events   → tabela de movimentação
 */
const PERIOD_OPTIONS = [
    { id: "3m", label: "3 meses", months: 3 },
    { id: "6m", label: "6 meses", months: 6 },
    { id: "12m", label: "12 meses", months: 12 },
    { id: "ytd", label: "Este ano", months: null }, // computed
];

const FILTER_OPTIONS = [
    { id: "all", label: "Todos" },
    { id: "new", label: "Novas Assinaturas" },
    { id: "upgrade", label: "Upgrades" },
    { id: "cancel", label: "Cancelamentos" },
];

const EVENT_META = {
    new: { icon: "🟢", label: "Nova assinatura", color: "text-emerald-300" },
    upgrade: { icon: "🔼", label: "Upgrade", color: "text-indigo-300" },
    downgrade: { icon: "🔽", label: "Downgrade", color: "text-amber-300" },
    cancel: { icon: "🔴", label: "Cancelamento", color: "text-red-300" },
};

const monthLabel = (d) => d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
const brl = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;

// ─── History computation ─────────────────────────────────────────────────
// For each month, MRR at end = sum of active registrations whose createdAt <= end and !cancelled.
const buildHistory = (registrations, months) => {
    const now = new Date();
    // Start of current month
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    // Determine how many months to render
    const monthsCount = months ?? (now.getMonth() + 1);
    const result = [];
    for (let i = monthsCount - 1; i >= 0; i--) {
        const monthStart = new Date(startMonth.getFullYear(), startMonth.getMonth() - i, 1);
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0, 23, 59, 59);
        let mrr = 0;
        registrations.forEach((r) => {
            if (r.createdAt > monthEnd.getTime()) return;
            if (r.status === "cancelled") return; // simplification (no cancellation date stored)
            mrr += r.mrr || 0;
        });
        result.push({
            key: `${monthStart.getFullYear()}-${monthStart.getMonth()}`,
            month: monthLabel(monthStart),
            mrr,
            projected: mrr * 12,
        });
    }
    // Compute change % vs previous month for tooltip.
    return result.map((r, idx) => {
        const prev = idx > 0 ? result[idx - 1].mrr : null;
        const changePct = prev == null || prev === 0 ? null : ((r.mrr - prev) / prev) * 100;
        return { ...r, changePct };
    });
};

// ─── Event derivation ────────────────────────────────────────────────────
// Since we don't have full status_history, we synthesize:
//   - "new" event for each registration (at createdAt)
//   - "cancel" event for status === "cancelled" (createdAt + 20d as approximation)
//   - a couple of synthetic upgrades/downgrades derived from plan tier for demo purposes
const PLAN_TIER = { Classic: 1, Pro: 1, Free: 0, Royal: 2, Premium: 2, Imperial: 3, Enterprise: 3 };

// ─── Churn-risk detection ────────────────────────────────────────────────
const TRIAL_LENGTH_DAYS = 14;
const DAY = 24 * 60 * 60 * 1000;

const detectChurnRisk = (registrations) => {
    const now = Date.now();
    const items = [];
    registrations.forEach((r) => {
        if (r.status === "cancelled") return;
        const ageDays = Math.floor((now - r.createdAt) / DAY);

        if (r.status === "trial") {
            const daysLeft = TRIAL_LENGTH_DAYS - ageDays;
            if (daysLeft < 0) {
                items.push({
                    id: r.id,
                    tenant: r.shopName,
                    shopId: r.shopId,
                    plan: r.plan,
                    phone: r.phone,
                    ownerName: r.ownerName,
                    reason: "Trial vencido",
                    severity: "high",
                    detail: `Trial acabou há ${Math.abs(daysLeft)} dia(s)`,
                });
            } else if (daysLeft <= 3) {
                items.push({
                    id: r.id,
                    tenant: r.shopName,
                    shopId: r.shopId,
                    plan: r.plan,
                    phone: r.phone,
                    ownerName: r.ownerName,
                    reason: "Trial expirando",
                    severity: "medium",
                    detail: `${daysLeft} dia(s) para converter`,
                });
            }
            return;
        }

        if (r.status === "suspended") {
            items.push({
                id: r.id,
                tenant: r.shopName,
                shopId: r.shopId,
                plan: r.plan,
                phone: r.phone,
                ownerName: r.ownerName,
                reason: "Conta suspensa",
                severity: "high",
                detail: `Suspensa há ${ageDays} dia(s)`,
            });
            return;
        }

        // Ativo sem MRR por > 30 dias (sinal de plano free/pausado)
        if (r.status === "active" && (r.mrr || 0) === 0 && ageDays > 30) {
            items.push({
                id: r.id,
                tenant: r.shopName,
                shopId: r.shopId,
                plan: r.plan,
                phone: r.phone,
                ownerName: r.ownerName,
                reason: "Sem faturamento (30d+)",
                severity: "low",
                detail: `${ageDays} dias sem MRR`,
            });
        }
    });
    // Sort by severity high > medium > low
    const rank = { high: 0, medium: 1, low: 2 };
    items.sort((a, b) => rank[a.severity] - rank[b.severity]);
    const byBucket = {
        high: items.filter((i) => i.severity === "high").length,
        medium: items.filter((i) => i.severity === "medium").length,
        low: items.filter((i) => i.severity === "low").length,
    };
    return { items, total: items.length, byBucket };
};
const buildEvents = (registrations) => {
    const evts = [];
    registrations.forEach((r, idx) => {
        // New subscription
        evts.push({
            id: `${r.id}-new`,
            date: r.createdAt,
            tenant: r.shopName,
            shopId: r.shopId,
            type: "new",
            plan: r.plan,
            value: r.mrr || 0,
            mrrImpact: r.mrr || 0,
        });
        // Cancellation
        if (r.status === "cancelled") {
            evts.push({
                id: `${r.id}-cancel`,
                date: (r.cancelledAt || r.createdAt + 20 * 24 * 60 * 60 * 1000),
                tenant: r.shopName,
                shopId: r.shopId,
                type: "cancel",
                plan: r.plan,
                value: 0,
                mrrImpact: -(r.mrr || 0),
            });
        }
        // A single synthetic upgrade every 3rd active registration for demo realism
        if (idx % 3 === 2 && r.status === "active" && (PLAN_TIER[r.plan] || 1) < 3) {
            const upliftMap = { 1: 100, 2: 100 };
            const uplift = upliftMap[PLAN_TIER[r.plan] || 1] || 100;
            evts.push({
                id: `${r.id}-up`,
                date: r.createdAt + 10 * 24 * 60 * 60 * 1000,
                tenant: r.shopName,
                shopId: r.shopId,
                type: "upgrade",
                plan: r.plan,
                value: uplift,
                mrrImpact: uplift,
            });
        }
    });
    return evts.sort((a, b) => b.date - a.date);
};

const AdminBilling = () => {
    const { registrations } = useSalon();
    const [period, setPeriod] = useState("12m");
    const [filter, setFilter] = useState("all");

    // KPIs
    const summary = useMemo(() => {
        const now = Date.now();
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
        const mrr = registrations
            .filter((r) => r.status !== "cancelled")
            .reduce((s, r) => s + (r.mrr || 0), 0);
        const active = registrations.filter((r) => r.status === "active").length;
        const trial = registrations.filter((r) => r.status === "trial").length;
        const churn = registrations.filter((r) => r.status === "cancelled" && (now - (r.cancelledAt || r.createdAt)) <= THIRTY_DAYS).length;
        return { mrr, active, trial, churn, arr: mrr * 12 };
    }, [registrations]);

    // History (chart)
    const history = useMemo(() => {
        const opt = PERIOD_OPTIONS.find((p) => p.id === period);
        return buildHistory(registrations, opt.months);
    }, [registrations, period]);

    // Revenue by plan
    const revenueByPlan = useMemo(() => {
        const map = {};
        registrations
            .filter((r) => r.status !== "cancelled")
            .forEach((r) => {
                const plan = r.plan || "Free";
                if (!map[plan]) map[plan] = { plan, tenants: 0, mrr: 0 };
                map[plan].tenants += 1;
                map[plan].mrr += r.mrr || 0;
            });
        const arr = Object.values(map).sort((a, b) => b.mrr - a.mrr);
        const totalMrr = arr.reduce((s, p) => s + p.mrr, 0) || 1;
        return arr.map((p) => ({ ...p, pct: (p.mrr / totalMrr) * 100 }));
    }, [registrations]);

    // Events (for month filter table)
    const allEvents = useMemo(() => buildEvents(registrations), [registrations]);
    const monthEvents = useMemo(() => {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).getTime();
        return allEvents
            .filter((e) => e.date >= monthStart && e.date <= monthEnd)
            .filter((e) => filter === "all" || e.type === filter);
    }, [allEvents, filter]);

    // Churn-risk detection (heurística leve — endpoint futuro: GET /admin/mrr/churn-risk)
    const churnRisk = useMemo(() => detectChurnRisk(registrations), [registrations]);

    const handleExport = () => {
        toast.info("Exportação", {
            description: "CSV gerado em /reports/mrr-events.csv (demo).",
        });
    };

    return (
        <div data-testid="admin-billing-view" className="space-y-6">
            {/* ─── Header ─── */}
            <header className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400">
                        Módulo 02 · Plataforma
                    </div>
                    <h1 className="salon-heading text-3xl text-white mt-1">Faturamento &amp; MRR</h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Receita recorrente, evolução mensal e movimentações da plataforma.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Period selector */}
                    <div
                        data-testid="admin-billing-period"
                        className="flex items-center gap-1 bg-slate-950/60 rounded-xl p-1 border border-white/5"
                    >
                        {PERIOD_OPTIONS.map((p) => (
                            <button
                                key={p.id}
                                data-testid={`admin-billing-period-${p.id}`}
                                onClick={() => setPeriod(p.id)}
                                className={`min-h-[40px] px-3 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all ${
                                    period === p.id
                                        ? "bg-indigo-500 text-white"
                                        : "text-slate-400 hover:text-white"
                                }`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button
                        data-testid="admin-billing-export"
                        onClick={handleExport}
                        className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-indigo-500/60 text-slate-300 hover:text-white text-xs uppercase tracking-wider transition-all"
                    >
                        <i className="fa-solid fa-file-export mr-2"></i>Exportar CSV
                    </button>
                </div>
            </header>

            {/* ─── SEÇÃO 1: KPIs ─── */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <AdminKpi label="MRR Total" value={brl(summary.mrr)} icon="fa-sack-dollar" color="#6366f1" testid="kpi-mrr" />
                <AdminKpi label="Tenants Ativos" value={summary.active} icon="fa-circle-check" color="#10b981" testid="kpi-active" />
                <AdminKpi label="Em Trial" value={summary.trial} icon="fa-clock" color="#f59e0b" testid="kpi-trial" />
                <AdminKpi label="Churn do Mês" value={summary.churn} icon="fa-arrow-trend-down" color="#ef4444" testid="kpi-churn" />
                <AdminKpi label="ARR Projetado" value={brl(summary.arr)} icon="fa-chart-line" color="#8b5cf6" testid="kpi-arr" />
            </div>

            {/* ─── Alerta de Churn (Enhancement) ─── */}
            <ChurnRiskCard risk={churnRisk} />

            {/* ─── SEÇÃO 2: Chart ─── */}
            <section
                data-testid="admin-billing-chart"
                className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5"
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-400">Evolução</div>
                        <h2 className="salon-heading text-xl text-white mt-0.5">MRR mês a mês</h2>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-400">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2 w-4 rounded bg-indigo-500"></span> MRR real
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-0 w-4 border-t-2 border-violet-400 border-dashed"></span> Projetado (ARR÷12)
                        </span>
                    </div>
                </div>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={history} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#6366f1" stopOpacity={0.55} />
                                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#ffffff10" strokeDasharray="3 6" />
                            <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                            <YAxis
                                stroke="#94a3b8"
                                tick={{ fontSize: 11 }}
                                tickFormatter={(v) => `R$${v}`}
                            />
                            <Tooltip content={<MrrTooltip />} />
                            <Area type="monotone" dataKey="mrr" stroke="#6366f1" strokeWidth={2.5} fill="url(#mrrFill)" />
                            <Line
                                type="monotone"
                                dataKey="projected"
                                stroke="#a78bfa"
                                strokeWidth={1.5}
                                strokeDasharray="6 4"
                                dot={false}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* ─── SEÇÃO 3: Receita por Plano ─── */}
            <section
                data-testid="admin-billing-by-plan"
                className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5"
            >
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-400">Distribuição</div>
                        <h2 className="salon-heading text-xl text-white mt-0.5">Receita por Plano</h2>
                    </div>
                    <div className="text-[11px] text-slate-400">
                        {revenueByPlan.length} planos ativos · {revenueByPlan.reduce((s, p) => s + p.tenants, 0)} tenants
                    </div>
                </div>
                {revenueByPlan.length === 0 ? (
                    <div className="py-8 text-center text-slate-500">
                        <i className="fa-solid fa-chart-simple text-2xl opacity-40 mb-2"></i>
                        <p className="text-sm">Nenhum tenant faturado ainda.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {revenueByPlan.map((p) => (
                            <PlanBar key={p.plan} plan={p} />
                        ))}
                    </div>
                )}
            </section>

            {/* ─── SEÇÃO 4: Movimentação do Mês ─── */}
            <section
                data-testid="admin-billing-events"
                className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl overflow-hidden"
            >
                <div className="p-5 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-indigo-400">Movimentação</div>
                        <h2 className="salon-heading text-xl text-white mt-0.5">Eventos do Mês</h2>
                    </div>
                    <div className="flex items-center gap-1 bg-slate-950/60 rounded-xl p-1 border border-white/5">
                        {FILTER_OPTIONS.map((f) => (
                            <button
                                key={f.id}
                                data-testid={`admin-billing-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`min-h-[36px] px-3 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-all ${
                                    filter === f.id
                                        ? "bg-indigo-500 text-white"
                                        : "text-slate-400 hover:text-white"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                                <th className="text-left py-3 px-4">Data</th>
                                <th className="text-left py-3 px-4">Salão / Tenant</th>
                                <th className="text-left py-3 px-4">Evento</th>
                                <th className="text-left py-3 px-4">Plano</th>
                                <th className="text-right py-3 px-4">Valor</th>
                                <th className="text-right py-3 px-4">Impacto MRR</th>
                            </tr>
                        </thead>
                        <tbody>
                            {monthEvents.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center text-slate-500">
                                        <i className="fa-solid fa-inbox text-3xl mb-3 opacity-40"></i>
                                        <p>Nenhum evento neste filtro.</p>
                                    </td>
                                </tr>
                            )}
                            {monthEvents.map((e) => (
                                <EventRow key={e.id} event={e} />
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-3 border-t border-white/5 text-[11px] text-slate-500 flex items-center justify-between">
                    <span>
                        <span className="text-white font-mono">{monthEvents.length}</span> evento(s) no mês atual
                    </span>
                    <span>
                        <i className="fa-solid fa-database mr-1.5"></i>Fonte: `registrations` (localStorage)
                    </span>
                </div>
            </section>
        </div>
    );
};

// ─── Sub-components ──────────────────────────────────────────────────────

const AdminKpi = ({ label, value, icon, color = "#6366f1", testid }) => (
    <div
        data-testid={testid}
        className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-4"
    >
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
            <div
                className="h-8 w-8 rounded-lg flex items-center justify-center text-sm"
                style={{ background: `${color}22`, color }}
            >
                <i className={`fa-solid ${icon}`}></i>
            </div>
        </div>
        <div className="salon-heading text-2xl text-white mt-2">{value}</div>
    </div>
);

const PlanColors = {
    Classic: "#94a3b8",
    Pro: "#6366f1",
    Free: "#64748b",
    Royal: "#8b5cf6",
    Premium: "#a78bfa",
    Imperial: "#f59e0b",
    Enterprise: "#f97316",
};

const PlanBar = ({ plan }) => {
    const color = PlanColors[plan.plan] || "#6366f1";
    return (
        <div
            data-testid={`admin-billing-plan-${plan.plan}`}
            className="rounded-xl border border-white/5 bg-slate-950/40 p-3"
        >
            <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }}></span>
                    <span className="text-sm font-bold text-white uppercase tracking-wider">{plan.plan}</span>
                    <span className="text-[10px] text-slate-500 font-mono">
                        {plan.tenants} tenant{plan.tenants === 1 ? "" : "s"}
                    </span>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                    <span className="text-slate-400">
                        <span className="text-white font-mono font-bold">{brl(plan.mrr)}</span> MRR
                    </span>
                    <span
                        className="px-2 py-0.5 rounded-md text-[10px] font-bold"
                        style={{ background: `${color}22`, color }}
                    >
                        {plan.pct.toFixed(1)}%
                    </span>
                </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                        width: `${Math.max(plan.pct, 3)}%`,
                        background: `linear-gradient(90deg, ${color}, ${color}80)`,
                    }}
                ></div>
            </div>
        </div>
    );
};

const EventRow = ({ event }) => {
    const meta = EVENT_META[event.type] || EVENT_META.new;
    const positive = event.mrrImpact > 0;
    const negative = event.mrrImpact < 0;
    return (
        <tr
            data-testid={`admin-billing-event-${event.id}`}
            className="border-t border-white/5 hover:bg-white/5 transition-colors"
        >
            <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">{formatDate(event.date)}</td>
            <td className="py-3 px-4">
                <div className="text-white text-sm font-semibold">{event.tenant}</div>
                <div className="text-[11px] text-slate-500 font-mono">/{event.shopId}</div>
            </td>
            <td className="py-3 px-4">
                <span className={`inline-flex items-center gap-2 text-xs font-bold ${meta.color}`}>
                    <span>{meta.icon}</span>
                    {meta.label}
                </span>
            </td>
            <td className="py-3 px-4">
                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-bold border border-white/10 text-slate-300">
                    {event.plan}
                </span>
            </td>
            <td className="py-3 px-4 text-right font-mono text-white text-sm">
                {event.value ? brl(event.value) : <span className="text-slate-600">—</span>}
            </td>
            <td
                className={`py-3 px-4 text-right font-mono font-bold text-sm ${
                    positive ? "text-emerald-400" : negative ? "text-red-400" : "text-slate-500"
                }`}
            >
                {positive ? "+" : ""}
                {event.mrrImpact ? brl(event.mrrImpact) : "—"}
            </td>
        </tr>
    );
};

const MrrTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
        <div className="rounded-xl border border-indigo-500/40 bg-slate-950/95 backdrop-blur-xl px-3 py-2 shadow-2xl">
            <div className="text-[10px] uppercase tracking-[0.2em] text-indigo-300 font-bold mb-1">{label}</div>
            <div className="text-white font-mono text-lg font-bold">{brl(d.mrr)}</div>
            {d.changePct != null && (
                <div
                    className={`text-[11px] font-mono ${
                        d.changePct > 0 ? "text-emerald-400" : d.changePct < 0 ? "text-red-400" : "text-slate-400"
                    }`}
                >
                    {d.changePct > 0 ? "▲ +" : d.changePct < 0 ? "▼ " : ""}
                    {d.changePct.toFixed(1)}% vs mês anterior
                </div>
            )}
            <div className="text-[10px] text-violet-300/80 mt-1 font-mono">
                ARR proj: {brl(d.projected)}
            </div>
        </div>
    );
};

// ─── Churn Risk Card ─────────────────────────────────────────────────────
const RISK_BUCKETS = [
    { key: "high", label: "Crítico", color: "#ef4444", icon: "fa-fire" },
    { key: "medium", label: "Atenção", color: "#f59e0b", icon: "fa-triangle-exclamation" },
    { key: "low", label: "Monitorar", color: "#64748b", icon: "fa-eye" },
];

// Per-severity retention action (opens WhatsApp with pre-filled template).
const RETENTION_ACTIONS = {
    high: {
        label: "Cupom de Retenção",
        icon: "fa-gift",
        color: "#ef4444",
        template: (item) =>
            `Oi ${item.ownerName?.split(" ")[0] || item.tenant}! Aqui é da equipe Kinng.app 👑\n\n` +
            `Vi que seu trial do plano ${item.plan} do salão ${item.tenant} acabou. ` +
            `Preparei um cupom exclusivo com 30% OFF nos 3 primeiros meses caso queira continuar 💛\n\n` +
            `Posso liberar seu acesso agora?`,
        toast: "Cupom de retenção",
    },
    medium: {
        label: "Lembrar Conversão",
        icon: "fa-bell",
        color: "#f59e0b",
        template: (item) =>
            `Oi ${item.ownerName?.split(" ")[0] || item.tenant}! 👋\n\n` +
            `Passando pra avisar que seu trial no Kinng.app termina em breve. ` +
            `Que tal a gente marcar 10 minutinhos pra você ver o painel de faturamento consolidado do ${item.tenant}? ` +
            `Prometo que vale a pena.`,
        toast: "Lembrete de conversão",
    },
    low: {
        label: "Ping de Reativação",
        icon: "fa-paper-plane",
        color: "#94a3b8",
        template: (item) =>
            `Oi ${item.ownerName?.split(" ")[0] || item.tenant}!\n\n` +
            `Notamos que o ${item.tenant} não tem gerado receita no Kinng.app. ` +
            `Posso te ajudar a montar o setup em 15min pra você começar a faturar mais rápido?`,
        toast: "Ping de reativação",
    },
};

const openWhatsApp = (item, template) => {
    const digits = (item.phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(template(item));
    // Fallback: even without phone, open wa.me picker so user chooses contact
    const url = digits
        ? `https://wa.me/${digits}?text=${msg}`
        : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
};

const ChurnRiskCard = ({ risk }) => {
    const [expanded, setExpanded] = useState(false);

    const handleAction = (item) => {
        const action = RETENTION_ACTIONS[item.severity];
        if (!action) return;
        openWhatsApp(item, action.template);
        toast.success(`${action.toast} enviado`, {
            description: `Mensagem preparada para ${item.tenant}. WhatsApp aberto em nova aba.`,
        });
    };

    const clean = risk.total === 0;
    return (
        <section
            data-testid="admin-billing-churn-risk"
            className={`rounded-2xl border overflow-hidden transition-all ${
                clean
                    ? "border-emerald-500/20 bg-emerald-500/5"
                    : "border-red-500/25 bg-gradient-to-br from-red-500/10 via-slate-900/40 to-slate-900/40"
            } backdrop-blur-xl`}
        >
            <button
                onClick={() => setExpanded((v) => !v)}
                disabled={clean}
                data-testid="admin-billing-churn-toggle"
                className={`w-full p-5 flex items-center gap-4 text-left ${clean ? "cursor-default" : "hover:bg-white/[0.03]"} transition-colors`}
            >
                <div
                    className={`h-12 w-12 rounded-2xl flex items-center justify-center text-lg shrink-0 ${
                        clean
                            ? "bg-emerald-500/15 border border-emerald-400/40 text-emerald-300"
                            : "bg-red-500/15 border border-red-400/40 text-red-300 animate-pulse"
                    }`}
                >
                    <i className={`fa-solid ${clean ? "fa-shield-check" : "fa-triangle-exclamation"}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-300/80 mb-0.5">
                        {clean ? "Sinal antecipado" : "Alerta de Churn"}
                    </div>
                    {clean ? (
                        <div className="text-white salon-heading text-lg">
                            Nenhum tenant em risco no momento
                        </div>
                    ) : (
                        <div className="flex items-baseline gap-3 flex-wrap">
                            <div className="text-white salon-heading text-2xl">
                                {risk.total} tenant{risk.total === 1 ? "" : "s"} em risco
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px]">
                                {RISK_BUCKETS.map((b) =>
                                    risk.byBucket[b.key] > 0 ? (
                                        <span
                                            key={b.key}
                                            data-testid={`admin-billing-churn-bucket-${b.key}`}
                                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md font-bold"
                                            style={{ background: `${b.color}22`, color: b.color }}
                                        >
                                            <i className={`fa-solid ${b.icon} text-[9px]`}></i>
                                            {risk.byBucket[b.key]} {b.label}
                                        </span>
                                    ) : null
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!clean && (
                    <div className="text-red-300/70 text-xs uppercase tracking-wider font-bold flex items-center gap-2 shrink-0">
                        {expanded ? "Ocultar" : "Ver lista"}
                        <i className={`fa-solid fa-chevron-${expanded ? "up" : "down"}`}></i>
                    </div>
                )}
            </button>

            {!clean && expanded && (
                <div className="border-t border-white/5 divide-y divide-white/5">
                    {risk.items.slice(0, 5).map((item) => {
                        const bucket = RISK_BUCKETS.find((b) => b.key === item.severity) || RISK_BUCKETS[2];
                        const action = RETENTION_ACTIONS[item.severity];
                        return (
                            <div
                                key={item.id}
                                data-testid={`admin-billing-churn-item-${item.id}`}
                                className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.03] transition-colors"
                            >
                                <div
                                    className="h-8 w-8 rounded-lg flex items-center justify-center text-xs shrink-0"
                                    style={{ background: `${bucket.color}22`, color: bucket.color }}
                                >
                                    <i className={`fa-solid ${bucket.icon}`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-white text-sm font-semibold truncate">
                                        {item.tenant}
                                        <span className="ml-2 text-[10px] text-slate-500 font-mono">/{item.shopId}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-400">
                                        <span style={{ color: bucket.color }} className="font-bold">
                                            {item.reason}
                                        </span>
                                        <span className="mx-1.5 text-slate-600">·</span>
                                        {item.detail}
                                    </div>
                                </div>
                                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-bold border border-white/10 text-slate-300 shrink-0">
                                    {item.plan}
                                </span>
                                {action && (
                                    <button
                                        data-testid={`admin-billing-churn-action-${item.id}`}
                                        onClick={() => handleAction(item)}
                                        title={`Enviar via WhatsApp para ${item.ownerName || item.tenant}`}
                                        className="min-h-[36px] shrink-0 inline-flex items-center gap-2 px-3 rounded-lg border text-[11px] font-bold uppercase tracking-wider transition-all hover:scale-[1.02] active:scale-95"
                                        style={{
                                            borderColor: `${action.color}55`,
                                            background: `${action.color}18`,
                                            color: action.color,
                                        }}
                                    >
                                        <i className={`fa-solid ${action.icon}`}></i>
                                        <span className="hidden md:inline">{action.label}</span>
                                        <i className="fa-brands fa-whatsapp text-xs opacity-70"></i>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {risk.items.length > 5 && (
                        <div className="px-5 py-2.5 text-center text-[11px] text-slate-500">
                            + {risk.items.length - 5} outro(s) — priorize os {RISK_BUCKETS[0].label.toLowerCase()} primeiro.
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default AdminBilling;